import { Typography } from "@bb-ui/react-library/dist/components/Typography";
import { createStyles, Grid, makeStyles } from "@material-ui/core";
import CopyrightIcon from "@material-ui/icons/Copyright";
import clsx from "clsx";
import * as React from "react";

export interface LogoProps {
  title?: string;
  size?: "small" | "medium" | "large" | "xlarge";
  copyright?: boolean;
}

export const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      fontSize: "21px",
      lineHeight: "100%",
      height: "auto",
    },
    logo: {
      width: "115px",
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
    copyright: {
      marginRight: 2,
    },
    smallLogo: {
      width: "90px",
    },
    mediumLogo: {
      width: "115px",
    },
    largeLogo: {
      width: "175px",
    },
    xlargeLogo: {
      width: 275,
    },
    smallCopyright: {
      fontSize: "14px",
    },
    mediumCopyright: {
      fontSize: "14px",
    },
    largeCopyright: {
      fontSize: "14px",
    },
    xlargeCopyright: {
      fontSize: "14px",
    },
  })
);

export const Logo: React.FunctionComponent<LogoProps> = (props) => {
  const classes = useStyles(props);
  let logoSize = classes.mediumLogo;
  let copyrightSize = classes.mediumCopyright;

  if (props.size === "small") {
    logoSize = classes.smallLogo;
    copyrightSize = classes.smallCopyright;
  }

  if (props.size === "large") {
    logoSize = classes.largeLogo;
    copyrightSize = classes.largeCopyright;
  }

  if (props.size === "xlarge") {
    logoSize = classes.xlargeLogo;
    copyrightSize = classes.xlargeCopyright;
  }

  return (
    <Grid container alignItems="center" className={classes.root} {...props}>
      {(() =>
        props.copyright && (
          <CopyrightIcon
            fontSize="inherit"
            className={clsx(classes.copyright, copyrightSize)}
          />
        ))()}
      <svg
        viewBox="0 0 175 25"
        xmlns="http://www.w3.org/2000/svg"
        aria-labelledby="blackboard-logo"
        className={clsx(classes.logo, logoSize)}
        preserveAspectRatio="xMidYMid meet"
      >
        <title id="blackboard-logo">Blackboard</title>
        {/* eslint-disable-next-line max-len */}
        <path
          fill="currentColor"
          d="M18.014 6.76c0 2.783-1.583 4.208-3.306 5.077 2.709.91 4.598 2.43 4.598 5.539 0 4.072-3.403 6.244-8.57 6.244H0V.964h10.389c4.667 0 7.625 2.24 7.625 5.796zm-4.083.652c0-1.846-1.459-2.919-4.07-2.919H4v6.014h5.556c2.625 0 4.375-.991 4.375-3.095zm1.291 9.543c0-1.941-1.555-3.068-4.764-3.068H4v6.204h6.75c2.792 0 4.472-1.073 4.472-3.136zM22.014 23.62V0h4v23.62h-4zM40.278 23.62v-2.104c-1.195 1.384-3.014 2.457-5.667 2.457-3.32 0-6.222-1.846-6.222-5.28v-.069c0-3.678 3.014-5.497 7.083-5.497 2.125 0 3.472.285 4.834.706v-.408c0-2.362-1.528-3.651-4.306-3.651-1.944 0-3.403.407-5 1.059l-1.097-3.136c1.93-.842 3.805-1.426 6.653-1.426 5.166 0 7.68 2.648 7.68 7.209v10.126h-3.958v.014zm.097-7.249c-1.028-.38-2.444-.679-4-.679-2.514 0-4 1.005-4 2.756v.068c0 1.615 1.486 2.525 3.375 2.525 2.653 0 4.625-1.453 4.625-3.598v-1.072zM55.444 24c-5.194 0-9.041-3.978-9.041-8.864 0-4.887 3.833-8.96 9.07-8.96 3.305 0 5.36 1.195 6.985 2.946l-2.486 2.62c-1.222-1.263-2.514-2.131-4.528-2.131-2.916 0-5.027 2.457-5.027 5.47 0 3.068 2.152 5.525 5.222 5.525 1.889 0 3.278-.869 4.542-2.131l2.416 2.335c-1.722 1.873-3.75 3.19-7.153 3.19zM80.958 23.62h-4.625l-5.264-7.385-2.444 2.498v4.887h-4V0h4v14.104l7.25-7.575h4.861l-6.944 6.95 7.166 10.14zM93.042 23.973c-2.875 0-4.709-1.425-5.93-3.014v2.66h-4V0h4v9.38c1.29-1.778 3.11-3.204 5.93-3.204 4.138 0 8.139 3.204 8.139 8.892.013 5.701-3.959 8.905-8.14 8.905zM92.125 9.57c-2.708 0-5.097 2.172-5.097 5.498s2.389 5.498 5.097 5.498c2.75 0 5-2.104 5-5.498-.014-3.353-2.292-5.498-5-5.498zM111.945 24c-5.334 0-9.264-3.978-9.264-8.864 0-4.914 3.972-8.96 9.333-8.96 5.361 0 9.306 3.978 9.306 8.892 0 4.9-3.973 8.932-9.375 8.932zm0-14.39c-3.209 0-5.264 2.458-5.264 5.471 0 3.041 2.208 5.525 5.333 5.525 3.236 0 5.292-2.457 5.292-5.47 0-3.041-2.223-5.525-5.361-5.525zM134.417 23.62v-2.104c-1.195 1.384-3 2.457-5.653 2.457-3.306 0-6.222-1.846-6.222-5.28v-.069c0-3.678 3.014-5.497 7.083-5.497 2.111 0 3.472.285 4.833.706v-.408c0-2.362-1.527-3.651-4.305-3.651-1.945 0-3.417.407-5 1.059l-1.097-3.136c1.916-.842 3.805-1.426 6.652-1.426 5.167 0 7.681 2.648 7.681 7.209v10.126h-3.972v.014zm.097-7.249c-1.028-.38-2.444-.679-4-.679-2.514 0-4 1.005-4 2.756v.068c0 1.615 1.486 2.525 3.375 2.525 2.653 0 4.639-1.453 4.639-3.598v-1.072h-.014zM145.514 23.62h-4V6.53h4v3.855c1.097-2.552 3.111-4.303 6.194-4.168v4.14h-.236c-3.5 0-5.958 2.227-5.958 6.76v6.503zM166.208 23.62v-2.85c-1.291 1.777-3.111 3.203-5.958 3.203-4.111 0-8.111-3.204-8.111-8.892 0-5.7 3.93-8.89 8.111-8.89 2.875 0 4.694 1.424 5.958 2.999V0h4v23.62h-4zm-5-14.05c-2.75 0-5.027 2.077-5.027 5.498 0 3.366 2.319 5.498 5.027 5.498 2.681 0 5.07-2.172 5.07-5.498-.014-3.326-2.403-5.498-5.07-5.498zM175 1.385c0 .76-.625 1.384-1.417 1.384-.777 0-1.416-.624-1.416-1.384 0-.76.625-1.385 1.416-1.385.778 0 1.417.61 1.417 1.385zm-2.542 0c0 .61.514 1.126 1.125 1.126.625 0 1.139-.502 1.139-1.126 0-.625-.5-1.114-1.139-1.114a1.102 1.102 0 00-1.125 1.114zm1.223-.733c.375 0 .541.135.541.448 0 .149-.041.23-.166.325.111.068.125.109.139.38.013.272.013.272.111.326h-.417c-.042-.095-.042-.122-.042-.244-.014-.299-.014-.312-.222-.312h-.25v.556h-.347V.651h.653zm-.32.624h.264c.195 0 .236-.04.236-.163 0-.122-.041-.163-.236-.163h-.264v.326z"
        />
      </svg>
      {(() =>
        props.title && (
          <Typography variant="h1" aria-label={props.title}>
            {props.title}
          </Typography>
        ))()}
    </Grid>
  );
};
